import React from 'react';
import { useFormContext } from "react-hook-form";
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';

interface DateFieldProps {
  name: string;
  label: string;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
  icon?: React.ReactNode;
  captionLayout?: 'buttons' | 'dropdown';
  disabled?: boolean;
  // Let a form match its own field spacing without moving the other DateField forms
  className?: string;
  labelClassName?: string;
}

export const DateField = ({ 
  name, 
  label, 
  required, 
  minDate, 
  maxDate, 
  icon = <CalendarIcon size={16} />, 
  captionLayout = 'dropdown',
  disabled = false,
  className,
  labelClassName,
}: DateFieldProps) => {
  const { setValue, watch, formState: { errors } } = useFormContext();
  const value = watch(name);

  const handleDateSelect = (date: Date | undefined) => {
    if (disabled) return;
    
    if (date) {
      setValue(name, date.toISOString());
    } else {
      setValue(name, undefined);
    }
  };

  return (
    <div className={cn("form-group", className)}>
      <Label className={cn(
        "form-label flex items-center gap-2",
        disabled && "text-muted-foreground",
        labelClassName
      )}>
        {icon}
        {label}
        {required && <span className="text-red-500">*</span>}
      </Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            disabled={disabled}
            className={cn(
              "w-full justify-start text-left font-normal",
              !value && "text-muted-foreground",
              disabled && "bg-gray-100 cursor-not-allowed opacity-50"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(new Date(value), "PPP", { locale: es }) : "Seleccionar fecha"}
          </Button>
        </PopoverTrigger>
        {!disabled && (
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={value ? new Date(value) : undefined}
              onSelect={handleDateSelect}
              initialFocus
              captionLayout={captionLayout}
              fromYear={1950}
              toYear={2030}
              disabled={(date) => {
                if (minDate && date < minDate) return true;
                if (maxDate && date > maxDate) return true;
                return false;
              }}
            />
            {errors[name] && (
              <p className="text-red-500 text-sm">
                {errors[name]?.message as string}
              </p>
            )}
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
};